import { useState, useEffect } from 'react';
import useDebounce from '../hooks/useDebounce';
import {
    Box, Heading, Table, Thead, Tbody, Tr, Th, Td,
    Button, HStack, useToast, Spinner, Center,
    Card, CardBody, Modal, ModalOverlay, ModalContent,
    ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
    FormControl, FormLabel, Input, useDisclosure,
    InputGroup, InputLeftElement, Flex, IconButton, Tooltip, TableContainer
, Text } from '@chakra-ui/react';
import { FiPlus, FiEdit, FiSearch, FiSliders } from 'react-icons/fi';
import { authAPI } from '../services/api';
import InstitutionFeatureSwitches from '../components/InstitutionFeatureSwitches';

const InstitutionsPage = () => {
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [institutions, setInstitutions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentInstId, setCurrentInstId] = useState(null);
    const [formData, setFormData] = useState({ name: '', code: '', address: '' });
    const [submitLoading, setSubmitLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    // 功能授予 modal（超級管理員決定「這間機構可以用哪些功能」）
    const [featureInst, setFeatureInst] = useState(null);   // 目前編輯中的機構（null = 未開啟）
    const [grantDraft, setGrantDraft] = useState({});       // 草稿，按下儲存才送出
    const [grantSaving, setGrantSaving] = useState(false);
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    const fetchInstitutions = async () => {
        try {
            setLoading(true);
            const res = await authAPI.getInstitutions();
            // Ensure response is array
            setInstitutions(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error(error);
            toast({
                title: "無法取得機構列表",
                status: "error",
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInstitutions();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleEdit = (inst) => {
        setIsEditMode(true);
        setCurrentInstId(inst.id);
        setFormData({
            name: inst.name,
            code: inst.code,
            address: inst.address || ''
        });
        onOpen();
    };

    const openFeatures = (inst) => {
        setFeatureInst(inst);
        // 後端回傳的 granted_features 已是「預設 ∪ override」的完整 dict，直接當草稿
        setGrantDraft({ ...(inst.granted_features || {}) });
    };

    const saveFeatures = async () => {
        setGrantSaving(true);
        try {
            await authAPI.updateInstitution(featureInst.id, { granted_features: grantDraft });
            toast({ title: '功能授權已更新', status: 'success', duration: 2000 });
            setFeatureInst(null);
            fetchInstitutions();
        } catch (error) {
            toast({
                title: '更新失敗',
                description: error.response?.data?.detail || '請稍後再試',
                status: 'error',
            });
        } finally {
            setGrantSaving(false);
        }
    };

    const handleCloseModal = () => {
        setIsEditMode(false);
        setCurrentInstId(null);
        setFormData({ name: '', code: '', address: '' });
        onClose();
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.code) {
            toast({ title: "機構名稱與代碼為必填", status: "warning" });
            return;
        }

        try {
            setSubmitLoading(true);

            if (isEditMode) {
                // Update
                const res = await authAPI.updateInstitution(currentInstId, formData);
                toast({ title: "機構更新成功", status: "success" });

                // Optimistic UI: Map update
                const updatedInst = res.data;
                setInstitutions(prev => prev.map(inst => inst.id === currentInstId ? updatedInst : inst));
            } else {
                // Create
                const res = await authAPI.createInstitution(formData);
                toast({ title: "機構新增成功", status: "success" });

                // Optimistic UI: Append
                const newInst = res.data;
                setInstitutions(prev => [...prev, newInst]);
            }

            handleCloseModal();
            // No fetchInstitutions() calls here!
        } catch (error) {
            console.error(error);
            const msg = error.response?.data?.detail || (isEditMode ? "更新失敗" : "新增失敗");
            toast({ title: isEditMode ? "更新失敗" : "新增失敗", description: msg, status: "error" });
        } finally {
            setSubmitLoading(false);
        }
    };

    const filteredInstitutions = institutions.filter(inst => {
        const term = debouncedSearchTerm.toLowerCase();
        return inst.name.toLowerCase().includes(term) || inst.code.toLowerCase().includes(term);
    });

    if (loading) {
        return <Center h="200px"><Spinner /></Center>;
    }

    return (
        <Flex direction="column" h="100%" w="100%" minW="0" overflow="hidden">
            <Box flexShrink={0} mb={4}>
                <HStack justifyContent="space-between" mb={6}>
                    <Heading size="lg" color="brand.800">機構管理 (Super Admin)</Heading>
                    <Button leftIcon={<FiPlus />} colorScheme="brand" onClick={() => { handleCloseModal(); onOpen(); }}>
                        新增機構
                    </Button>
                </HStack>

                <HStack mb={4} spacing={4}>
                    <InputGroup maxW="300px">
                        <InputLeftElement pointerEvents="none">
                            <FiSearch color="gray.300" />
                        </InputLeftElement>
                        <Input
                            placeholder="搜尋機構名稱或代碼..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            bg="white"
                        />
                    </InputGroup>
                </HStack>
            </Box>

            {/* Scrollable Table Container */}
            <Card w="100%" minW="0" flex="1" overflow="hidden" display="flex" flexDirection="column">
                <CardBody p={0} minW="0" overflow="hidden" display="flex" flexDirection="column">
                    <TableContainer flex="1" overflowY="auto" overflowX="auto" w="100%" maxW={{ base: 'calc(100vw - 32px)', md: '100%' }}>
                        <Table variant="simple" minW="800px" whiteSpace="nowrap">
                            <Thead bg="neutral.50" position="sticky" top={0} zIndex={1} shadow="sm">
                                <Tr>
                                    <Th whiteSpace="nowrap">機構名稱</Th>
                                    <Th whiteSpace="nowrap">代碼 (Code)</Th>
                                    <Th minW="200px">地址</Th>
                                    <Th whiteSpace="nowrap">操作</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {filteredInstitutions.map((inst) => (
                                    <Tr key={inst.id}>
                                        <Td fontWeight="bold" whiteSpace="nowrap">{inst.name}</Td>
                                        <Td whiteSpace="nowrap">{inst.code}</Td>
                                        <Td>{inst.address || '-'}</Td>
                                        <Td whiteSpace="nowrap">
                                            <HStack spacing={2}>
                                                <HStack spacing={2}>
                                                    <Tooltip label="編輯機構" placement="top" hasArrow>
                                                        <IconButton
                                                            icon={<FiEdit />}
                                                            size="sm"
                                                            colorScheme="teal"
                                                            variant="solid"
                                                            aria-label="編輯"
                                                            onClick={() => handleEdit(inst)}
                                                        />
                                                    </Tooltip>
                                                    {/* 功能授權：決定這間機構「可以用」哪些模組。
                                                        機構自己的管理員再從被授予的範圍內決定要不要開（見設定頁）。 */}
                                                    <Tooltip label="功能授權" placement="top" hasArrow>
                                                        <IconButton
                                                            icon={<FiSliders />}
                                                            size="sm"
                                                            colorScheme="purple"
                                                            variant="solid"
                                                            aria-label="功能授權"
                                                            onClick={() => openFeatures(inst)}
                                                        />
                                                    </Tooltip>
                                                </HStack>
                                            </HStack>
                                        </Td>
                                    </Tr>
                                ))}
                                {institutions.length === 0 && (
                                    <Tr>
                                        <Td colSpan={4} textAlign="center" py={8} color="neutral.500">
                                            尚無機構資料
                                        </Td>
                                    </Tr>
                                )}
                            </Tbody>
                        </Table>
                    </TableContainer>
                </CardBody>
            </Card>

            {/* 新增/編輯機構 Modal */}
            <Modal isOpen={isOpen} onClose={handleCloseModal} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{isEditMode ? "編輯機構" : "新增機構"}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        <FormControl isRequired mb={4}>
                            <FormLabel>機構名稱</FormLabel>
                            <Input name="name" placeholder="例如: 快樂全科班" value={formData.name} onChange={handleInputChange} />
                        </FormControl>

                        <FormControl isRequired mb={4} isDisabled={isEditMode}>
                            <FormLabel>機構代碼 (Code)</FormLabel>
                            <Input name="code" placeholder="英文數字組合，例如: HAPPY01" value={formData.code} onChange={handleInputChange} />
                        </FormControl>

                        <FormControl mb={4}>
                            <FormLabel>地址</FormLabel>
                            <Input name="address" placeholder="例如: 台北市信義區..." value={formData.address} onChange={handleInputChange} />
                        </FormControl>
                    </ModalBody>

                    <ModalFooter>
                        <Button onClick={handleCloseModal} mr={3}>取消</Button>
                        <Button colorScheme="brand" onClick={handleSubmit} isLoading={submitLoading}>
                            {isEditMode ? "儲存變更" : "確認新增"}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            {/* 功能授權 Modal（超級管理員）。改的是 granted_features＝「這間機構可以用哪些」，
                不是「現在開不開」——後者由機構自己的管理員在設定頁決定。 */}
            <Modal isOpen={!!featureInst} onClose={() => setFeatureInst(null)} size="lg" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>功能授權 — {featureInst?.name}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text fontSize="sm" color="neutral.500" mb={3}>
                            這裡決定這間機構<strong>可以使用</strong>哪些功能。
                            關閉的項目，機構自己的管理員在設定頁會看到「未授權」且無法啟用。
                        </Text>
                        <InstitutionFeatureSwitches
                            mode="grant"
                            granted={grantDraft}
                            onChange={(key, val) => setGrantDraft((d) => ({ ...d, [key]: val }))}
                            isDisabled={grantSaving}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={() => setFeatureInst(null)}>取消</Button>
                        <Button colorScheme="brand" onClick={saveFeatures} isLoading={grantSaving}>儲存</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Flex>
    );
};

export default InstitutionsPage;
